import type { Locale } from "./locale";

// Every user-facing string the child actually sees, per language.
//
// Anything that puts a drawing's name into a sentence takes the drawing's
// GENDER, not just its name. English gets away with "a Dog" / "an Apple" by
// looking at the first letter; French needs "un Chien" / "une Maison", and the
// noun's gender is a property of the noun, not of its spelling. So `Gender`
// travels with the translated name (see drawings.fr.ts) and the sentence
// builders below take it. Getting this wrong is the classic i18n bug — it reads
// fine to a non-speaker and wrong to everyone else.

// Greek adds a third gender (neuter), so this is m/f/n. French only ever sees
// m/f; its ternaries fall through to masculine for "n", which never occurs in
// French data. Greek sentence builders don't branch on gender at all — the
// accusative article is stored with the noun (see accusativeOf / NAMES_EL).
export type Gender = "m" | "f" | "n";

export interface Strings {
  brandTagline: string;
  paperNoteStrong: string;
  paperNoteRest: string;

  whyDraw: string;
  whyDrawAria: string;
  funFacts: string;
  funFactsAria: string;
  myStickers: string;
  myStickersAria: string;

  pickCta: string;
  chooseDifficulty: string;
  levelEasy: string;
  levelMedium: string;
  levelHarder: string;
  levelDrawingsAria: (label: string) => string;

  flags: string;
  flagsAria: string;
  filterByCategory: string;
  catAll: string;
  catAllAria: string;
  catShapes: string;
  catShapesAria: string;
  catPaintings: string;
  catPaintingsAria: string;

  settings: string;
  forGrownUps: string;
  forGrownUpsAria: string;
  printGroupAria: string;
  printMaking: string;
  printCta: string;
  scrollUp: string;
  scrollDown: string;

  // Player
  home: string;
  backToHome: string;
  homeShapes: string;
  homePaintings: string;
  homeFlags: string;
  homeStickers: string;
  backToShapes: string;
  backToPaintings: string;
  backToFlags: string;
  backToStickers: string;
  by: string;
  howToDraw: (subject: string, gender: Gender) => string;
  stepOf: (i: number, n: number) => string;
  previousStep: string;
  nextStep: string;
  playDrawing: string;
  pauseDrawing: string;
  drawingSpeed: string;
  modeAnimate: string;
  modeStill: string;
  switchToStill: string;
  switchToAnimation: string;
  hintKeysBefore: string;
  hintKeysMiddle: string;
  hintKeysAfter: string;
  watchStart: string;
  watchStop: string;
  watchStartAria: string;
  watchStopAria: string;
  yourSketch: string;
  realPainting: string;

  // Spoken + shown finished lines
  allDoneWatchAgain: string;
  allDoneSurprise: string;
  /** Finished message in export/recording mode — no "press ▶" (there are no
   *  buttons in a video). */
  allDoneExport: string;
  nextColour: string;

  // Celebration
  youDidIt: string;
  /** Sentence prefix; the drawing's name follows in <strong>, then the suffix. */
  youDrew: (name: string, gender: Gender) => string;
  youDrewSuffix: string;
  levelUpTitle: (level: string) => string;
  /** Follows the drawing's name: "… — and finished every Easy drawing! 🥳" */
  levelUpAllDone: (level: string) => string;
  levelUpReady: (level: string) => string;
  newFriend: string;
  /** Sentence prefix; the reward's name follows in <strong>. `article` is the
   *  definite article that name needs ("le ", "la ", "l'") — empty in English. */
  youEarned: (article: string) => string;
  /** Follows the reward's name. French wants a space before "!". */
  youEarnedSuffix: string;
  /** Button in the celebration that starts drawing the freshly-won sticker.
   *  `article`/`name` name the friend ("Draw the Lion!" / "Dessine le Lion !"). */
  drawYourReward: (article: string, name: string) => string;
  pickAnother: string;
  watchAgain: string;
  nextLabel: string;
  startingIn: (secs: number) => string;
  goBackToDrawing: string;

  // Toggles
  voiceOn: string;
  voiceOff: string;
  voiceOnAria: string;
  voiceOffAria: string;
  voiceUnsupported: string;
  voiceLegendSayName: string;
  soundsOn: string;
  soundsOff: string;
  soundsOnAria: string;
  soundsOffAria: string;
  readAloudOn: string;
  readAloudOff: string;
  readAloudOnAria: string;
  readAloudOffAria: string;
  readAloudConfirm: string;
  autoPlayOn: string;
  autoPlayOff: string;
  autoPlayOnAria: string;
  autoPlayOffAria: string;
  themeLight: string;
  themeDark: string;
  themeLightAria: string;
  themeDarkAria: string;

  /** Locale switcher button label, e.g. "Passer en anglais". */
  switchTo: (languageName: string) => string;
  /** The name of each language, written in THIS language (not its endonym):
   *  the French UI must say "anglais", not "English". */
  languageName: Record<Locale, string>;


  // --- Sub-page chrome ------------------------------------------------------
  forGrownUpsLead: string;
  factsTitle: string;
  whyTitle: string;
  whySubtitle: string;
  whyIntro: string;
  whyOutro: string;
  privacyTitle: string;
  privacySubtitle: string;
  privacyGrownUps: string;
  termsTitle: string;
  termsSubtitle: string;
  termsGrownUps: string;
  contactTitle: string;
  contactSubtitle: string;
  shapesTitle: string;
  shapesSubtitle: string;
  flagsTitle: string;
  flagsSubtitle: string;
  paintingsTitle: string;
  paintingsSubtitle: string;

  // --- Contact form ---------------------------------------------------------
  feedbackAria: string;
  feedbackTitle: string;
  feedbackIntro: string;
  feedbackSendAnother: string;
  feedbackRatingPick: string;
  /** Visible labels only — the submitted `value` stays English so the
   *  site owner's inbox reads consistently across languages. */
  feedbackRatings: [string, string, string, string, string];
  feedbackErrMsg: string;
  feedbackThanks: string;
  feedbackHowFun: string;
  feedbackMessage: string;
  feedbackMessagePlaceholder: string;
  feedbackName: string;
  feedbackNamePlaceholder: string;
  feedbackEmail: string;
  feedbackSending: string;
  feedbackSend: string;
  feedbackError: string;
  madeWithLove: string;

  // --- Trophies -------------------------------------------------------------
  trophyTitle: string;
  trophyTally: (done: number, all: number) => string;
  trophyAllDone: string;
  trophyKeepGoing: string;
  trophyTapHint: string;
  trophyResetConfirm: string;
  trophyResetYes: string;
  trophyResetNo: string;
  trophyResetBtn: string;
  trophyFriends: string;
  /** `article` is the definite article the name needs; empty in English. */
  trophyFriendsNext: (n: number, article: string, name: string, emoji: string) => string;
  trophyFriendsAll: string;
  trophyTapFriend: string;
  trophyDrawingsToUnlock: (n: number) => string;
  trophyDrawFriendAria: (article: string, name: string) => string;
  trophyDrawFriendTitle: (article: string, name: string) => string;
  trophyFriendLocked: (name: string, need: number) => string;
  trophyCups: string;
  trophyCupsNext: (n: number, article: string, name: string, emoji: string) => string;
  trophyCupsAll: string;
  trophyCupLocked: (name: string, need: number) => string;
  trophyCupFlags: (need: number) => string;
  trophyGroupEasy: string;
  trophyGroupMedium: string;
  trophyGroupHarder: string;
  trophyGroupPaintings: string;
  trophyGroupFlags: string;
  cardDrawAria: (gender: Gender, name: string) => string;
  cardDrawDoneAria: (gender: Gender, name: string) => string;
  trophyStickerDone: (name: string) => string;
  trophyStickerLockedAria: (gender: Gender, name: string) => string;

  // --- Footer ---------------------------------------------------------------
  footerPrivacy: string;
  footerHello: string;
  footerRules: string;
  footerAbout: string;
  youtubeChannel: string;
  coffeeBtn: string;
  coffeeNote: string;
  footerLove: string;
  footerCopyright: (year: number) => string;

  // --- Grown-up controls ----------------------------------------------------
  tlCodeTitle: string;
  tlCodeEnter: string;
  tlWrongCode: string;
  tlSetCodeTitle: string;
  tlMakeCode: string;
  tlConfirmCode: string;
  tlCodesMismatch: string;
  tlLimitTitle: string;
  /** The toggle's off/on. */
  tlOff: string;
  tlOn: string;
  /** The "no daily limit" preset — a different word from the toggle's "off". */
  tlLimitOff: string;
  tlMin: (m: number) => string;
  tlUsedToday: string;
  tlOfLimit: (m: number) => string;
  tlNoLimit: string;
  tlKeepFullscreen: string;
  tlFsUnsupported: string;
  tlFewer: string;
  tlFewerNote: string;
  tlLockSettings: string;
  tlResetTimer: string;
  tlNewCodeOnDone: string;
  tlChangeCode: string;
  tlDone: string;
  tlClose: string;
  tlEscHint: string;
  lockTimesUpAria: string;
  lockTimesUp: string;
  lockTimesUpBody: string;
  lockEnterCode: string;
  lockWrongCode: string;
  fsAria: string;
  fsTitle: string;
  fsBody: string;
  fsEnterCode: string;
  exitAria: string;
  exitTitle: string;
  exitBody: string;
  pinDelete: string;
  pinHint: (first: string, sum: number) => string;
  lockKeepGoing: string;
  lockGrownUpCode: string;
  fsBackBtn: string;
  fsGrownUpExit: string;
  exitBtn: string;
  exitKeepDrawing: string;

  /** Spoken command words, per action. Lower-case, accent-free where possible. */
  commands: {
    up: string[];
    down: string[];
    shapes: string[];
    facts: string[];
    why: string[];
    paintings: string[];
    flags: string[];
    trophies: string[];
    print: string[];
    privacy: string[];
    contact: string[];
    terms: string[];
    dark: string[];
    light: string[];
    home: string[];
    back: string[];
    next: string[];
  };
}

const en: Strings = {
  brandTagline:
    "Let's learn to draw, step by step — a free drawing site for kids 💛",
  paperNoteStrong: "Grab a pen and paper!",
  paperNoteRest:
    " The screen is just the teacher — watch it draw, then draw it for real on paper. That's where the magic happens. ✨",

  whyDraw: "Why Draw?",
  whyDrawAria: "Why drawing is great",
  funFacts: "Fun Facts",
  funFactsAria: "Fun facts",
  myStickers: "My Stickers",
  myStickersAria: "My stickers",

  pickCta: "Pick something to draw 👇",
  chooseDifficulty: "Choose a difficulty",
  levelEasy: "Easy",
  levelMedium: "Medium",
  levelHarder: "Harder",
  levelDrawingsAria: (label) => `${label} drawings`,

  flags: "Flags",
  flagsAria: "Flags of the world",
  filterByCategory: "Filter easy drawings by category",
  catAll: "All",
  catAllAria: "All easy drawings",
  catShapes: "Shapes",
  catShapesAria: "Shapes",
  catPaintings: "Painting",
  catPaintingsAria: "Famous paintings",

  settings: "⚙️ Settings",
  forGrownUps: "For grown-ups",
  forGrownUpsAria: "For grown-ups — daily time limit and screen lock",
  printGroupAria: "For grown-ups — print coloring pages",
  printMaking: "⏳ Making PDF…",
  printCta: "🖨️ Print coloring pages",
  scrollUp: "Scroll up",
  scrollDown: "Scroll down",

  home: "🏠 Home",
  backToHome: "Back to home",
  homeShapes: "⭕ Shapes",
  homePaintings: "🖼️ Paintings",
  homeFlags: "🏳️ Flags",
  homeStickers: "🏆 Stickers",
  backToShapes: "Back to shapes",
  backToPaintings: "Back to paintings",
  backToFlags: "Back to flags",
  backToStickers: "Back to my stickers",
  by: "by",
  howToDraw: (subject) => {
    // Vowel letters usually take "an", but some start with a consonant *sound*
    // spelled with a vowel ("a unicorn", "a European flag") — keep those "a".
    const consonantSound = /^(uni|use|used|useful|eu|ewe|one)/i.test(subject);
    const article = !consonantSound && /^[aeiou]/i.test(subject) ? "an" : "a";
    return `How to draw ${article} ${subject}`;
  },
  stepOf: (i, n) => `Step ${i} / ${n}`,
  previousStep: "Previous step",
  nextStep: "Next step",
  playDrawing: "Play the drawing",
  pauseDrawing: "Pause the drawing",
  drawingSpeed: "Drawing speed",
  modeAnimate: "Animation",
  modeStill: "Still picture",
  switchToStill: "Show a still picture",
  switchToAnimation: "Watch it draw itself",
  hintKeysBefore: "Press ",
  hintKeysMiddle: " for next, ",
  hintKeysAfter: " to go back",
  watchStart: "Auto Play",
  watchStop: "Stop",
  watchStartAria: "Start hands-free auto play",
  watchStopAria: "Stop hands-free auto play",
  yourSketch: "Your sketch ✏️",
  realPainting: "The real painting 🖼️",

  allDoneWatchAgain: "🎉 All done! Let's watch it again…",
  allDoneSurprise: "Yay, all done! ⭐ Press ▶ for a surprise! 🎉",
  allDoneExport: "Yay, all done! 🎉",
  nextColour: "Now the next colour! 🖍️",

  youDidIt: "You did it!",
  youDrew: (name) => {
    const consonantSound = /^(uni|use|used|useful|eu|ewe|one)/i.test(name);
    const article = !consonantSound && /^[aeiou]/i.test(name) ? "an" : "a";
    return `You drew ${article} `;
  },
  youDrewSuffix: "! 🥳",
  levelUpTitle: (level) => `${level} mode complete! 🏆`,
  levelUpAllDone: (level) => ` — and finished every ${level} drawing! 🥳`,
  levelUpReady: (level) => `Ready for ${level} mode? Let's go! 🚀`,
  newFriend: "New friend unlocked! 🎉",
  youEarned: () => "You earned the ",
  youEarnedSuffix: "!",
  drawYourReward: (_article, name) => `Draw your ${name}! ✏️`,
  pickAnother: "🎨 Pick another",
  watchAgain: "↺ Watch again",
  nextLabel: "Next:",
  startingIn: (secs) => `starting in ${secs}s`,
  goBackToDrawing: "Go back to the drawing",

  voiceOn: "Listening…",
  voiceOff: "Talk to me",
  voiceOnAria: "Turn off voice control",
  voiceOffAria: "Turn on voice control",
  voiceUnsupported: "🎤 Voice isn't supported in this browser",
  voiceLegendSayName: "Say a name",
  soundsOn: "Sounds on",
  soundsOff: "Sounds off",
  soundsOnAria: "Turn off sounds",
  soundsOffAria: "Turn on sounds",
  readAloudOn: "Read aloud",
  readAloudOff: "Read off",
  readAloudOnAria: "Turn off reading aloud",
  readAloudOffAria: "Turn on reading aloud",
  autoPlayOn: "Auto Play on",
  autoPlayOff: "Auto Play off",
  autoPlayOnAria: "Turn off auto play",
  autoPlayOffAria: "Turn on auto play",
  readAloudConfirm: "Read aloud on!",
  themeLight: "☀️ Light",
  themeDark: "🌙 Dark",
  themeLightAria: "Switch to light theme",
  themeDarkAria: "Switch to dark theme",

  switchTo: (name) => `Switch to ${name}`,
  languageName: { en: "English", fr: "French", el: "Greek", nl: "Dutch" },

  forGrownUpsLead: "For grown-ups:",
  factsTitle: "💡 Fun Drawing Facts",
  whyTitle: "🎨 Why Drawing is Great",
  whySubtitle: "Little scribbles, big benefits! 🌱",
  whyIntro:
    "Drawing is one of the richest things a young child can do, and it quietly builds skills that matter well beyond art.",
  whyOutro:
    "The best part: there's no wrong way to do it. Praise the effort and the trying, hand over the crayons, and let them go. 💛",
  privacyTitle: "🔒 Your Privacy",
  privacySubtitle: "Safe, simple, and just for fun! 🌈",
  privacyGrownUps:
    "Learn 2 Draw is a static website with no backend, no cookies, and no advertising or cross-site trackers. Progress and the parental controls (PIN, daily time limit, full-screen lock) are stored only in this browser's local storage and are never transmitted. We use Cloudflare Web Analytics — a privacy-friendly, cookieless service that counts page views in aggregate and does not collect personal data or build visitor profiles. If you use the feedback form, the name, email and message you enter are sent to us by email via Web3Forms (a form delivery service) only when you submit it. The optional voice control uses the browser's built-in Web Speech API, which may send audio to the browser vendor for recognition while it's on. The site is hosted on Firebase Hosting, which processes visitors' IP addresses in standard server logs as the host.",
  termsTitle: "📜 Friendly Rules",
  termsSubtitle: "A few happy house rules 🌟",
  termsGrownUps:
    'Learn 2 Draw is provided free of charge, "as is", for personal, non-commercial use, without warranty of any kind. All source code and original artwork are the property of the author and may not be reproduced, redistributed or used commercially without written permission. The bundled painting images are public-domain reproductions. By using the site you accept that it is a playful educational tool and that the author is not liable for any loss arising from its use.',
  contactTitle: "👋 Say Hello!",
  contactSubtitle: "A little note from us to you 💛",
  shapesTitle: "⭕ Learn Your Shapes",
  shapesSubtitle:
    "Big, simple shapes for little artists — draw one giant shape at a time! 🔺🟦⭕",
  flagsTitle: "🏳️ Flags of the World",
  flagsSubtitle: "Draw a flag, one shape at a time! 🖍️",
  paintingsTitle: "🖼️ Famous Paintings",
  paintingsSubtitle: "Make a masterpiece, one sketch at a time! 🎨",

  feedbackAria: "Send us feedback",
  feedbackTitle: "💌 Tell us what you think",
  feedbackIntro:
    "Grown-ups: we'd love to hear how we can make Learn 2 Draw even better!",
  feedbackSendAnother: "Send another",
  feedbackRatingPick: "— pick one (optional) —",
  feedbackRatings: [
    "⭐⭐⭐⭐⭐ Loved it",
    "⭐⭐⭐⭐ Really good",
    "⭐⭐⭐ It's okay",
    "⭐⭐ Needs work",
    "⭐ Not for us",
  ],
  feedbackErrMsg: "Oops — something went wrong. Please try again in a moment. 🙏",
  feedbackThanks: "Thank you so much! Your message is on its way. 💛",
  feedbackHowFun: "How fun was it?",
  feedbackMessage: "Your message",
  feedbackMessagePlaceholder: "What did you like? What could be better?",
  feedbackName: "Your name (optional)",
  feedbackNamePlaceholder: "Jamie",
  feedbackEmail: "Email (optional — so we can reply)",
  feedbackSending: "Sending… ✉️",
  feedbackSend: "Send feedback 💌",
  feedbackError: "Something went wrong. Please try again.",
  madeWithLove: "Made with ❤️ for happy little artists 🖍️",

  trophyTitle: "🏆 My Sticker Shelf",
  trophyTally: (done, all) => `You've earned ${done} of ${all} stickers!`,
  trophyAllDone: "🎉 You drew them all!",
  trophyKeepGoing: "Keep drawing to collect them! ✨",
  trophyTapHint: "Tap a faded one (or say its name) to draw it! ✏️",
  trophyResetConfirm: "Clear all your stickers and start over?",
  trophyResetYes: "Yes, reset",
  trophyResetNo: "Keep them",
  trophyResetBtn: "🔄 Start over",
  trophyFriends: "🎁 Animal Friends",
  trophyFriendsNext: (n, _article, name, emoji) =>
    `A new animal friend every 5 drawings — ${n} more to unlock the ${name}! ${emoji}`,
  trophyFriendsAll: "Wow! You collected every animal friend! 🦕🎉",
  trophyTapFriend: "Tap a friend to draw it! ✏️",
  trophyDrawingsToUnlock: (n) => `${n} drawings`,
  trophyDrawFriendAria: (_article, name) => `Draw the ${name}`,
  trophyDrawFriendTitle: (_article, name) => `Draw the ${name}! ✏️`,
  trophyFriendLocked: (name, need) => `${name} — draw ${need} to unlock`,
  trophyCups: "⚽ Football Cups",
  trophyCupsNext: (n, _article, name, emoji) =>
    `Win a cup for drawing World Cup flags — ${n} more for the ${name}! ${emoji}`,
  trophyCupsAll: "Champion! You won every football cup! 🏆🎉",
  trophyCupLocked: (name, need) => `${name} — draw ${need} World Cup flags to win`,
  trophyCupFlags: (need) => `${need} flags`,
  trophyGroupEasy: "🌱 Easy",
  trophyGroupMedium: "🌳 Medium",
  trophyGroupHarder: "⭐ Harder",
  trophyGroupPaintings: "🖼️ Paintings",
  trophyGroupFlags: "🏳️ Flags",
  cardDrawAria: (_gender, name) => `Draw a ${name}`,
  cardDrawDoneAria: (_gender, name) => `Draw a ${name} (done!)`,
  trophyStickerDone: (name) => `${name} — done!`,
  trophyStickerLockedAria: (_gender, name) => `Draw a ${name} — not done yet`,

  footerPrivacy: "🔒 Privacy",
  footerHello: "👋 Say hello",
  footerRules: "📜 Rules",
  footerAbout: "ℹ️ About",
  youtubeChannel: "📺 YouTube channel",
  coffeeBtn: "☕ Buy me a coffee",
  coffeeNote: "Love the site? Help keep it free & add more drawings 💛",
  footerLove: " — made with ❤️ for little artists, and built with them 🖍️",
  footerCopyright: (year) => `© ${year} Learn 2 Draw — all rights reserved.`,

  tlCodeTitle: "Grown-up code 🔒",
  tlCodeEnter: "Enter your 4-digit code to open the settings",
  tlWrongCode: "Wrong code",
  tlSetCodeTitle: "Set a grown-up code 🔒",
  tlMakeCode: "Make up a 4-digit code",
  tlConfirmCode: "Enter it again to confirm",
  tlCodesMismatch: "Codes didn't match — start again",
  tlLimitTitle: "⏱️ Daily time limit",
  tlOff: "Off",
  tlOn: "On",
  tlLimitOff: "Off",
  tlMin: (m) => `${m} min`,
  tlUsedToday: "Used today:",
  tlOfLimit: (m) => ` of ${m} min`,
  tlNoLimit: " (no limit set)",
  tlKeepFullscreen: "🖥️ Keep full screen",
  tlFsUnsupported: "Full screen isn't supported on this device.",
  tlFewer: "🙈 Fewer distractions",
  tlFewerNote:
    "Hides the footer links and “Print to color”. On automatically while a time limit or full screen is set.",
  tlLockSettings: "🔒 Lock settings with a code",
  tlResetTimer: "↺ Reset today's timer",
  tlNewCodeOnDone: "New code on Done",
  tlChangeCode: "Change code",
  tlDone: "Done",
  tlClose: "Close",
  tlEscHint: "Press Esc again to exit ✏️",
  lockTimesUpAria: "Time's up",
  lockTimesUp: "Time's up!",
  lockTimesUpBody: "Great drawing today! Ask a grown-up if you'd like to keep going.",
  lockEnterCode: "Enter the 4-digit code",
  lockWrongCode: "Wrong code — try again",
  fsAria: "Full screen",
  fsTitle: "Keep drawing!",
  fsBody: "Tap the button to go back to full screen.",
  fsEnterCode: "Enter the 4-digit code to exit",
  exitAria: "Exit full screen",
  exitTitle: "Exit full screen?",
  exitBody: "Enter the grown-up code to leave.",
  pinDelete: "Delete",
  pinHint: (first, sum) =>
    `Hint: starts with ${first}, and the four digits add up to ${sum}.`,
  lockKeepGoing: "🔓 Keep going",
  lockGrownUpCode: "🔓 Grown-up code",
  fsBackBtn: "▶ Back to full screen",
  fsGrownUpExit: "Grown-up: exit",
  exitBtn: "🔓 Exit full screen",
  exitKeepDrawing: "✏️ Keep drawing",

  commands: {
    up: ["up"],
    down: ["down"],
    shapes: ["shapes", "shape"],
    facts: ["facts", "fun facts"],
    why: ["why draw", "why drawing", "why"],
    paintings: ["paintings", "painting", "art", "famous"],
    flags: ["flags", "flag"],
    trophies: ["stickers", "sticker", "trophies", "trophy", "badges"],
    print: ["print", "coloring", "color me"],
    privacy: ["privacy"],
    contact: ["contact", "hello", "say hello"],
    terms: ["terms", "rules"],
    dark: ["dark", "dark mode", "night", "night mode"],
    light: ["light", "light mode", "day", "day mode"],
    home: ["home"],
    back: ["back"],
    next: ["next"],
  },
};

const fr: Strings = {
  brandTagline:
    "Apprenons à dessiner, étape par étape — un site de dessin gratuit pour les enfants 💛",
  paperNoteStrong: "Prends un crayon et du papier !",
  paperNoteRest:
    " L'écran n'est que le maître — regarde-le dessiner, puis dessine pour de vrai sur ton papier. C'est là que la magie opère. ✨",

  whyDraw: "Pourquoi dessiner ?",
  whyDrawAria: "Pourquoi le dessin, c'est super",
  funFacts: "Le savais-tu ?",
  funFactsAria: "Des faits amusants",
  myStickers: "Mes autocollants",
  myStickersAria: "Mes autocollants",

  pickCta: "Choisis quelque chose à dessiner 👇",
  chooseDifficulty: "Choisis un niveau",
  levelEasy: "Facile",
  levelMedium: "Moyen",
  levelHarder: "Difficile",
  levelDrawingsAria: (label) => `Dessins niveau ${label.toLowerCase()}`,

  flags: "Drapeaux",
  flagsAria: "Drapeaux du monde",
  filterByCategory: "Filtrer les dessins faciles par catégorie",
  catAll: "Tout",
  catAllAria: "Tous les dessins faciles",
  catShapes: "Formes",
  catShapesAria: "Formes",
  catPaintings: "Peinture",
  catPaintingsAria: "Tableaux célèbres",

  settings: "⚙️ Réglages",
  forGrownUps: "Pour les grands",
  forGrownUpsAria: "Pour les grands — limite de temps et verrouillage de l'écran",
  printGroupAria: "Pour les grands — imprimer des coloriages",
  printMaking: "⏳ Création du PDF…",
  printCta: "🖨️ Imprimer des coloriages",
  scrollUp: "Monter",
  scrollDown: "Descendre",

  home: "🏠 Accueil",
  backToHome: "Retour à l'accueil",
  homeShapes: "⭕ Formes",
  homePaintings: "🖼️ Tableaux",
  homeFlags: "🏳️ Drapeaux",
  homeStickers: "🏆 Autocollants",
  backToShapes: "Retour aux formes",
  backToPaintings: "Retour aux tableaux",
  backToFlags: "Retour aux drapeaux",
  backToStickers: "Retour à mes autocollants",
  by: "par",
  howToDraw: (subject, gender) =>
    `Comment dessiner ${gender === "f" ? "une" : "un"} ${subject}`,
  stepOf: (i, n) => `Étape ${i} / ${n}`,
  previousStep: "Étape précédente",
  nextStep: "Étape suivante",
  playDrawing: "Lancer le dessin",
  pauseDrawing: "Mettre le dessin en pause",
  drawingSpeed: "Vitesse du dessin",
  modeAnimate: "Animation",
  modeStill: "Image fixe",
  switchToStill: "Afficher une image fixe",
  switchToAnimation: "Regarder le dessin s'animer",
  hintKeysBefore: "Appuie sur ",
  hintKeysMiddle: " pour continuer, ",
  hintKeysAfter: " pour revenir",
  watchStart: "Lecture auto",
  watchStop: "Arrêter",
  watchStartAria: "Lancer la lecture automatique mains libres",
  watchStopAria: "Arrêter la lecture automatique mains libres",
  yourSketch: "Ton dessin ✏️",
  realPainting: "Le vrai tableau 🖼️",

  allDoneWatchAgain: "🎉 C'est fini ! On le regarde encore une fois…",
  allDoneSurprise: "Bravo, c'est fini ! ⭐ Appuie sur ▶ pour une surprise ! 🎉",
  allDoneExport: "Bravo, c'est fini ! 🎉",
  nextColour: "Et maintenant la couleur suivante ! 🖍️",

  youDidIt: "Tu as réussi !",
  youDrew: (_name, gender) => `Tu as dessiné ${gender === "f" ? "une" : "un"} `,
  youDrewSuffix: " ! 🥳",
  levelUpTitle: (level) => `Niveau ${level.toLowerCase()} terminé ! 🏆`,
  levelUpAllDone: (level) =>
    ` — et tu as fini tous les dessins ${level.toLowerCase()} ! 🥳`,
  levelUpReady: (level) => `Prêt pour le niveau ${level.toLowerCase()} ? On y va ! 🚀`,
  newFriend: "Un nouvel ami débloqué ! 🎉",
  youEarned: (article) => `Tu as gagné ${article}`,
  youEarnedSuffix: " !",
  drawYourReward: (article, name) => `Dessine ${article}${name} ! ✏️`,
  pickAnother: "🎨 Choisis-en un autre",
  watchAgain: "↺ Regarder encore",
  nextLabel: "Ensuite :",
  startingIn: (secs) => `dans ${secs} s`,
  goBackToDrawing: "Revenir au dessin",

  voiceOn: "J'écoute…",
  voiceOff: "Parle-moi",
  voiceOnAria: "Désactiver la commande vocale",
  voiceOffAria: "Activer la commande vocale",
  voiceUnsupported: "🎤 La voix n'est pas disponible dans ce navigateur",
  voiceLegendSayName: "Dis un nom",
  soundsOn: "Sons activés",
  soundsOff: "Sons coupés",
  soundsOnAria: "Couper les sons",
  soundsOffAria: "Activer les sons",
  readAloudOn: "Lecture à voix haute",
  readAloudOff: "Lecture coupée",
  readAloudOnAria: "Couper la lecture à voix haute",
  readAloudOffAria: "Activer la lecture à voix haute",
  autoPlayOn: "Lecture auto activée",
  autoPlayOff: "Lecture auto coupée",
  autoPlayOnAria: "Couper la lecture auto",
  autoPlayOffAria: "Activer la lecture auto",
  readAloudConfirm: "Lecture à voix haute activée !",
  themeLight: "☀️ Clair",
  themeDark: "🌙 Sombre",
  themeLightAria: "Passer au thème clair",
  themeDarkAria: "Passer au thème sombre",

  switchTo: (name) => `Passer en ${name}`,
  languageName: { en: "anglais", fr: "français", el: "grec", nl: "néerlandais" },

  forGrownUpsLead: "Pour les grands :",
  factsTitle: "💡 Le savais-tu ?",
  whyTitle: "🎨 Pourquoi dessiner, c'est super",
  whySubtitle: "Petits gribouillis, grands bienfaits ! 🌱",
  whyIntro:
    "Le dessin est l'une des activités les plus riches pour un jeune enfant, et il construit discrètement des compétences qui dépassent largement l'art.",
  whyOutro:
    "Le plus beau : il n'y a pas de mauvaise façon de faire. Félicitez l'effort et l'envie d'essayer, tendez les crayons, et laissez faire. 💛",
  privacyTitle: "🔒 Ta vie privée",
  privacySubtitle: "Sûr, simple, et juste pour s'amuser ! 🌈",
  privacyGrownUps:
    "Learn 2 Draw est un site statique sans serveur applicatif, sans cookies, sans publicité ni traceurs tiers. La progression et les contrôles parentaux (code, limite de temps quotidienne, verrouillage plein écran) sont enregistrés uniquement dans le stockage local de ce navigateur et ne sont jamais transmis. Nous utilisons Cloudflare Web Analytics — un service respectueux de la vie privée, sans cookies, qui compte les pages vues de façon agrégée, ne collecte aucune donnée personnelle et ne construit aucun profil de visiteur. Si vous utilisez le formulaire de contact, le nom, l'adresse e-mail et le message que vous saisissez nous sont envoyés par e-mail via Web3Forms (un service d'acheminement de formulaires), uniquement au moment de l'envoi. La commande vocale, facultative, utilise l'API Web Speech intégrée au navigateur, qui peut transmettre de l'audio à l'éditeur du navigateur pour la reconnaissance lorsqu'elle est activée. Le site est hébergé sur Firebase Hosting, qui traite les adresses IP des visiteurs dans ses journaux serveur standard en tant qu'hébergeur.",
  termsTitle: "📜 Les règles du jeu",
  termsSubtitle: "Quelques règles de la maison 🌟",
  termsGrownUps:
    "Learn 2 Draw est fourni gratuitement, « en l'état », pour un usage personnel et non commercial, sans garantie d'aucune sorte. L'ensemble du code source et des illustrations originales est la propriété de l'auteur et ne peut être reproduit, redistribué ou utilisé commercialement sans autorisation écrite. Les images de tableaux incluses sont des reproductions du domaine public. En utilisant le site, vous acceptez qu'il s'agisse d'un outil éducatif ludique et que l'auteur ne saurait être tenu responsable d'un quelconque préjudice lié à son utilisation.",
  contactTitle: "👋 Dis bonjour !",
  contactSubtitle: "Un petit mot de nous pour toi 💛",
  shapesTitle: "⭕ Apprends les formes",
  shapesSubtitle:
    "De grandes formes toutes simples pour les petits artistes — une seule forme géante à la fois ! 🔺🟦⭕",
  flagsTitle: "🏳️ Drapeaux du monde",
  flagsSubtitle: "Dessine un drapeau, une forme à la fois ! 🖍️",
  paintingsTitle: "🖼️ Tableaux célèbres",
  paintingsSubtitle: "Crée un chef-d'œuvre, un croquis à la fois ! 🎨",

  feedbackAria: "Envoyez-nous un message",
  feedbackTitle: "💌 Dites-nous ce que vous en pensez",
  feedbackIntro:
    "Chers parents : dites-nous comment rendre Learn 2 Draw encore meilleur !",
  feedbackSendAnother: "Envoyer un autre message",
  feedbackRatingPick: "— choisissez (facultatif) —",
  feedbackRatings: [
    "⭐⭐⭐⭐⭐ On a adoré",
    "⭐⭐⭐⭐ Très bien",
    "⭐⭐⭐ Correct",
    "⭐⭐ À améliorer",
    "⭐ Pas pour nous",
  ],
  feedbackErrMsg: "Oups — une erreur est survenue. Merci de réessayer dans un instant. 🙏",
  feedbackThanks: "Merci beaucoup ! Votre message est en route. 💛",
  feedbackHowFun: "C'était amusant ?",
  feedbackMessage: "Votre message",
  feedbackMessagePlaceholder: "Qu'avez-vous aimé ? Que pourrait-on améliorer ?",
  feedbackName: "Votre nom (facultatif)",
  feedbackNamePlaceholder: "Camille",
  feedbackEmail: "E-mail (facultatif — pour qu'on puisse répondre)",
  feedbackSending: "Envoi… ✉️",
  feedbackSend: "Envoyer 💌",
  feedbackError: "Une erreur est survenue. Merci de réessayer.",
  madeWithLove: "Fait avec ❤️ pour de joyeux petits artistes 🖍️",

  trophyTitle: "🏆 Mes autocollants",
  // French pluralises from 2, so 0 and 1 both take the singular.
  trophyTally: (done, all) =>
    `Tu as gagné ${done} autocollant${done > 1 ? "s" : ""} sur ${all} !`,
  trophyAllDone: "🎉 Tu les as tous dessinés !",
  trophyKeepGoing: "Continue de dessiner pour tous les collectionner ! ✨",
  trophyTapHint: "Touche un dessin pâle (ou dis son nom) pour le dessiner ! ✏️",
  trophyResetConfirm: "Effacer tous tes autocollants et recommencer ?",
  trophyResetYes: "Oui, tout effacer",
  trophyResetNo: "Les garder",
  trophyResetBtn: "🔄 Recommencer",
  trophyFriends: "🎁 Amis animaux",
  trophyFriendsNext: (n, article, name, emoji) =>
    `Un nouvel ami animal tous les 5 dessins — encore ${n} pour débloquer ${article}${name} ! ${emoji}`,
  trophyFriendsAll: "Bravo ! Tu as collectionné tous les amis animaux ! 🦕🎉",
  trophyTapFriend: "Touche un ami pour le dessiner ! ✏️",
  // French pluralises from 2, so 1 takes the singular.
  trophyDrawingsToUnlock: (n) => `${n} dessin${n > 1 ? "s" : ""}`,
  trophyDrawFriendAria: (article, name) => `Dessiner ${article}${name}`,
  trophyDrawFriendTitle: (article, name) => `Dessine ${article}${name} ! ✏️`,
  // "pour le/la débloquer" would need the pronoun to agree; sidestep it.
  trophyFriendLocked: (name, need) =>
    `${name} — à débloquer avec ${need} dessin${need > 1 ? "s" : ""}`,
  trophyCups: "⚽ Coupes de football",
  trophyCupsNext: (n, article, name, emoji) =>
    `Gagne une coupe en dessinant des drapeaux de la Coupe du monde — encore ${n} pour ${article}${name} ! ${emoji}`,
  trophyCupsAll: "Champion ! Tu as gagné toutes les coupes ! 🏆🎉",
  trophyCupLocked: (name, need) =>
    `${name} — dessine ${need} drapeaux de la Coupe du monde pour la gagner`,
  trophyCupFlags: (need) => `${need} drapeau${need > 1 ? "x" : ""}`,
  trophyGroupEasy: "🌱 Facile",
  trophyGroupMedium: "🌳 Moyen",
  trophyGroupHarder: "⭐ Difficile",
  trophyGroupPaintings: "🖼️ Tableaux",
  trophyGroupFlags: "🏳️ Drapeaux",
  cardDrawAria: (gender, name) => `Dessiner ${gender === "f" ? "une" : "un"} ${name}`,
  cardDrawDoneAria: (gender, name) =>
    `Dessiner ${gender === "f" ? "une" : "un"} ${name} (fait !)`,
  trophyStickerDone: (name) => `${name} — fait !`,
  trophyStickerLockedAria: (gender, name) =>
    `Dessiner ${gender === "f" ? "une" : "un"} ${name} — pas encore fait`,

  footerPrivacy: "🔒 Vie privée",
  footerHello: "👋 Dis bonjour",
  footerRules: "📜 Règles",
  footerAbout: "ℹ️ À propos",
  youtubeChannel: "📺 Chaîne YouTube",
  coffeeBtn: "☕ Offre-moi un café",
  coffeeNote:
    "Vous aimez le site ? Aidez-le à rester gratuit et à s'enrichir de nouveaux dessins 💛",
  footerLove: " — fait avec ❤️ pour les petits artistes, et construit avec eux 🖍️",
  footerCopyright: (year) => `© ${year} Learn 2 Draw — tous droits réservés.`,

  tlCodeTitle: "Code des grands 🔒",
  tlCodeEnter: "Saisissez votre code à 4 chiffres pour ouvrir les réglages",
  tlWrongCode: "Code incorrect",
  tlSetCodeTitle: "Créer un code des grands 🔒",
  tlMakeCode: "Choisissez un code à 4 chiffres",
  tlConfirmCode: "Saisissez-le à nouveau pour confirmer",
  tlCodesMismatch: "Les codes ne correspondent pas — recommencez",
  tlLimitTitle: "⏱️ Limite de temps quotidienne",
  tlOff: "Non",
  tlOn: "Oui",
  tlLimitOff: "Aucune",
  tlMin: (m) => `${m} min`,
  tlUsedToday: "Utilisé aujourd'hui :",
  tlOfLimit: (m) => ` sur ${m} min`,
  tlNoLimit: " (aucune limite définie)",
  tlKeepFullscreen: "🖥️ Rester en plein écran",
  tlFsUnsupported: "Le plein écran n'est pas disponible sur cet appareil.",
  tlFewer: "🙈 Moins de distractions",
  tlFewerNote:
    "Masque les liens de bas de page et « Imprimer des coloriages ». Activé automatiquement quand une limite de temps ou le plein écran est en place.",
  tlLockSettings: "🔒 Protéger les réglages par un code",
  tlResetTimer: "↺ Remettre le minuteur du jour à zéro",
  tlNewCodeOnDone: "Nouveau code à la validation",
  tlChangeCode: "Changer le code",
  tlDone: "Terminé",
  tlClose: "Fermer",
  tlEscHint: "Appuyez encore sur Échap pour quitter ✏️",
  lockTimesUpAria: "Temps écoulé",
  lockTimesUp: "C'est l'heure de s'arrêter !",
  lockTimesUpBody:
    "Tu as bien dessiné aujourd'hui ! Demande à un adulte si tu veux continuer.",
  lockEnterCode: "Saisissez le code à 4 chiffres",
  lockWrongCode: "Code incorrect — réessayez",
  fsAria: "Plein écran",
  fsTitle: "Continue de dessiner !",
  fsBody: "Touche le bouton pour revenir en plein écran.",
  fsEnterCode: "Saisissez le code à 4 chiffres pour quitter",
  exitAria: "Quitter le plein écran",
  exitTitle: "Quitter le plein écran ?",
  exitBody: "Saisissez le code des grands pour sortir.",
  pinDelete: "Supprimer",
  pinHint: (first, sum) =>
    `Indice : commence par ${first}, et la somme des quatre chiffres fait ${sum}.`,
  lockKeepGoing: "🔓 Continuer",
  lockGrownUpCode: "🔓 Code des grands",
  fsBackBtn: "▶ Revenir en plein écran",
  fsGrownUpExit: "Adulte : quitter",
  exitBtn: "🔓 Quitter le plein écran",
  exitKeepDrawing: "✏️ Continuer à dessiner",

  // Accent-free where possible so the matcher and the recognizer agree.
  commands: {
    up: ["haut", "monte"],
    down: ["bas", "descends"],
    shapes: ["formes", "forme"],
    facts: ["savais", "faits"],
    why: ["pourquoi"],
    paintings: ["peinture", "peintures", "tableau", "tableaux", "art"],
    flags: ["drapeaux", "drapeau"],
    trophies: ["autocollants", "autocollant", "trophees", "trophées"],
    print: ["imprimer", "coloriage"],
    privacy: ["confidentialite", "confidentialité"],
    contact: ["contact", "bonjour"],
    terms: ["regles", "règles", "conditions"],
    dark: ["sombre", "nuit"],
    light: ["clair", "jour"],
    home: ["accueil", "maison"],
    back: ["retour", "precedent", "précédent"],
    next: ["suivant", "suite"],
  },
};

const el: Strings = {
  brandTagline:
    "Ας μάθουμε να ζωγραφίζουμε, βήμα βήμα — ένας δωρεάν ιστότοπος ζωγραφικής για παιδιά 💛",
  paperNoteStrong: "Πάρε ένα μολύβι και χαρτί!",
  paperNoteRest:
    " Η οθόνη είναι απλώς ο δάσκαλος — δες την να ζωγραφίζει, και μετά ζωγράφισέ το στ' αλήθεια στο χαρτί σου. Εκεί γίνεται η μαγεία. ✨",

  whyDraw: "Γιατί να ζωγραφίζω;",
  whyDrawAria: "Γιατί η ζωγραφική είναι υπέροχη",
  funFacts: "Το ήξερες;",
  funFactsAria: "Διασκεδαστικά στοιχεία",
  myStickers: "Τα αυτοκόλλητά μου",
  myStickersAria: "Τα αυτοκόλλητά μου",

  pickCta: "Διάλεξε κάτι να ζωγραφίσεις 👇",
  chooseDifficulty: "Διάλεξε επίπεδο",
  levelEasy: "Εύκολο",
  levelMedium: "Μεσαίο",
  levelHarder: "Δύσκολο",
  levelDrawingsAria: (label) => `Σχέδια επιπέδου ${label.toLowerCase()}`,

  flags: "Σημαίες",
  flagsAria: "Σημαίες του κόσμου",
  filterByCategory: "Φιλτράρισε τα εύκολα σχέδια ανά κατηγορία",
  catAll: "Όλα",
  catAllAria: "Όλα τα εύκολα σχέδια",
  catShapes: "Σχήματα",
  catShapesAria: "Σχήματα",
  catPaintings: "Πίνακες",
  catPaintingsAria: "Διάσημοι πίνακες",

  settings: "⚙️ Ρυθμίσεις",
  forGrownUps: "Για τους μεγάλους",
  forGrownUpsAria: "Για τους μεγάλους — ημερήσιο όριο χρόνου και κλείδωμα οθόνης",
  printGroupAria: "Για τους μεγάλους — εκτύπωση σελίδων για χρωματισμό",
  printMaking: "⏳ Δημιουργία PDF…",
  printCta: "🖨️ Εκτύπωση σελίδων για χρωματισμό",
  scrollUp: "Πάνω",
  scrollDown: "Κάτω",

  home: "🏠 Αρχική",
  backToHome: "Πίσω στην αρχική",
  homeShapes: "⭕ Σχήματα",
  homePaintings: "🖼️ Πίνακες",
  homeFlags: "🏳️ Σημαίες",
  homeStickers: "🏆 Αυτοκόλλητα",
  backToShapes: "Πίσω στα σχήματα",
  backToPaintings: "Πίσω στους πίνακες",
  backToFlags: "Πίσω στις σημαίες",
  backToStickers: "Πίσω στα αυτοκόλλητά μου",
  by: "από",
  // Greek "draw" takes the accusative; the article + accusative noun arrive
  // ready-made in `subject` (see accusativeOf), so gender is unused here.
  howToDraw: (subject) => `Πώς να ζωγραφίσεις ${subject}`,
  stepOf: (i, n) => `Βήμα ${i} / ${n}`,
  previousStep: "Προηγούμενο βήμα",
  nextStep: "Επόμενο βήμα",
  playDrawing: "Ξεκίνα το σχέδιο",
  pauseDrawing: "Παύση σχεδίου",
  drawingSpeed: "Ταχύτητα σχεδίου",
  modeAnimate: "Κινούμενο",
  modeStill: "Ακίνητη εικόνα",
  switchToStill: "Δείξε ακίνητη εικόνα",
  switchToAnimation: "Δες το να ζωγραφίζεται",
  hintKeysBefore: "Πάτησε ",
  hintKeysMiddle: " για επόμενο, ",
  hintKeysAfter: " για να πας πίσω",
  watchStart: "Αυτόματο",
  watchStop: "Στοπ",
  watchStartAria: "Ξεκίνα την αυτόματη αναπαραγωγή χωρίς χέρια",
  watchStopAria: "Σταμάτα την αυτόματη αναπαραγωγή χωρίς χέρια",
  yourSketch: "Το σχέδιό σου ✏️",
  realPainting: "Ο αληθινός πίνακας 🖼️",

  allDoneWatchAgain: "🎉 Τελειώσαμε! Ας το δούμε ξανά…",
  allDoneSurprise: "Ζήτω, τελειώσαμε! ⭐ Πάτησε ▶ για μια έκπληξη! 🎉",
  allDoneExport: "Ζήτω, τελειώσαμε! 🎉",
  nextColour: "Τώρα το επόμενο χρώμα! 🖍️",

  youDidIt: "Τα κατάφερες!",
  // The drawing's name follows in <strong> as a labelled reveal (nominative,
  // like on the card), so no article/gender is needed in the prefix.
  youDrew: () => "Ζωγράφισες… ",
  youDrewSuffix: "! 🥳",
  levelUpTitle: (level) => `Ολοκλήρωσες το επίπεδο «${level}»! 🏆`,
  levelUpAllDone: (level) => ` — και τελείωσες όλα τα σχέδια «${level}»! 🥳`,
  levelUpReady: (level) => `Έτοιμος για το επίπεδο «${level}»; Πάμε! 🚀`,
  newFriend: "Ξεκλείδωσες έναν νέο φίλο! 🎉",
  youEarned: (article) => `Κέρδισες ${article}`,
  youEarnedSuffix: "!",
  drawYourReward: (article, name) => `Ζωγράφισε ${article}${name}! ✏️`,
  pickAnother: "🎨 Διάλεξε άλλο",
  watchAgain: "↺ Δες το ξανά",
  nextLabel: "Μετά:",
  startingIn: (secs) => `ξεκινά σε ${secs} δευτ.`,
  goBackToDrawing: "Επιστροφή στο σχέδιο",

  voiceOn: "Ακούω…",
  voiceOff: "Μίλα μου",
  voiceOnAria: "Απενεργοποίηση φωνητικού ελέγχου",
  voiceOffAria: "Ενεργοποίηση φωνητικού ελέγχου",
  voiceUnsupported: "🎤 Η φωνή δεν υποστηρίζεται σε αυτόν τον περιηγητή",
  voiceLegendSayName: "Πες ένα όνομα",
  soundsOn: "Ήχοι ανοιχτοί",
  soundsOff: "Ήχοι κλειστοί",
  soundsOnAria: "Κλείσε τους ήχους",
  soundsOffAria: "Άνοιξε τους ήχους",
  readAloudOn: "Ανάγνωση",
  readAloudOff: "Χωρίς φωνή",
  readAloudOnAria: "Κλείσε την ανάγνωση φωναχτά",
  readAloudOffAria: "Άνοιξε την ανάγνωση φωναχτά",
  autoPlayOn: "Αυτόματο ανοιχτό",
  autoPlayOff: "Αυτόματο κλειστό",
  autoPlayOnAria: "Κλείσε το αυτόματο",
  autoPlayOffAria: "Άνοιξε το αυτόματο",
  readAloudConfirm: "Η ανάγνωση φωναχτά είναι ανοιχτή!",
  themeLight: "☀️ Φωτεινό",
  themeDark: "🌙 Σκοτεινό",
  themeLightAria: "Άλλαξε σε φωτεινό θέμα",
  themeDarkAria: "Άλλαξε σε σκοτεινό θέμα",

  switchTo: (name) => `Άλλαξε στα ${name}`,
  languageName: { en: "Αγγλικά", fr: "Γαλλικά", el: "Ελληνικά", nl: "Ολλανδικά" },

  forGrownUpsLead: "Για τους μεγάλους:",
  factsTitle: "💡 Διασκεδαστικά στοιχεία για τη ζωγραφική",
  whyTitle: "🎨 Γιατί η ζωγραφική είναι υπέροχη",
  whySubtitle: "Μικρά μουτζουρώματα, μεγάλα οφέλη! 🌱",
  whyIntro:
    "Η ζωγραφική είναι από τα πιο πλούσια πράγματα που μπορεί να κάνει ένα μικρό παιδί, και χτίζει αθόρυβα δεξιότητες που ξεπερνούν κατά πολύ την τέχνη.",
  whyOutro:
    "Το καλύτερο: δεν υπάρχει λάθος τρόπος. Επαινέστε την προσπάθεια και τη διάθεση, δώστε τις μπογιές και αφήστε τα ελεύθερα. 💛",
  privacyTitle: "🔒 Η ιδιωτικότητά σου",
  privacySubtitle: "Ασφαλές, απλό και μόνο για διασκέδαση! 🌈",
  privacyGrownUps:
    "Το Learn 2 Draw είναι ένας στατικός ιστότοπος χωρίς διακομιστή εφαρμογής, χωρίς cookies, χωρίς διαφημίσεις ή ιχνηλάτες τρίτων. Η πρόοδος και οι γονικοί έλεγχοι (κωδικός, ημερήσιο όριο χρόνου, κλείδωμα πλήρους οθόνης) αποθηκεύονται μόνο στην τοπική μνήμη αυτού του περιηγητή και δεν μεταδίδονται ποτέ. Χρησιμοποιούμε το Cloudflare Web Analytics — μια υπηρεσία φιλική προς την ιδιωτικότητα, χωρίς cookies, που μετρά τις προβολές σελίδων συγκεντρωτικά και δεν συλλέγει προσωπικά δεδομένα ούτε δημιουργεί προφίλ επισκεπτών. Αν χρησιμοποιήσετε τη φόρμα επικοινωνίας, το όνομα, το email και το μήνυμα που πληκτρολογείτε μάς αποστέλλονται με email μέσω του Web3Forms (μιας υπηρεσίας παράδοσης φορμών) μόνο όταν το υποβάλλετε. Ο προαιρετικός φωνητικός έλεγχος χρησιμοποιεί το ενσωματωμένο Web Speech API του περιηγητή, το οποίο ενδέχεται να στέλνει ήχο στον κατασκευαστή του περιηγητή για αναγνώριση όσο είναι ενεργό. Ο ιστότοπος φιλοξενείται στο Firebase Hosting, το οποίο επεξεργάζεται τις διευθύνσεις IP των επισκεπτών στα τυπικά αρχεία καταγραφής του διακομιστή ως πάροχος φιλοξενίας.",
  termsTitle: "📜 Φιλικοί κανόνες",
  termsSubtitle: "Μερικοί χαρούμενοι κανόνες του σπιτιού 🌟",
  termsGrownUps:
    "Το Learn 2 Draw παρέχεται δωρεάν, «ως έχει», για προσωπική, μη εμπορική χρήση, χωρίς καμία εγγύηση οποιουδήποτε είδους. Όλος ο πηγαίος κώδικας και τα πρωτότυπα έργα τέχνης ανήκουν στον δημιουργό και δεν επιτρέπεται να αναπαραχθούν, να αναδιανεμηθούν ή να χρησιμοποιηθούν εμπορικά χωρίς γραπτή άδεια. Οι συμπεριλαμβανόμενες εικόνες πινάκων είναι αναπαραγωγές δημόσιας κτήσης. Χρησιμοποιώντας τον ιστότοπο, αποδέχεστε ότι πρόκειται για ένα παιχνιδιάρικο εκπαιδευτικό εργαλείο και ότι ο δημιουργός δεν φέρει ευθύνη για οποιαδήποτε ζημία προκύψει από τη χρήση του.",
  contactTitle: "👋 Πες γεια!",
  contactSubtitle: "Ένα μικρό σημείωμα από εμάς για σένα 💛",
  shapesTitle: "⭕ Μάθε τα σχήματα",
  shapesSubtitle:
    "Μεγάλα, απλά σχήματα για μικρούς καλλιτέχνες — ένα γιγάντιο σχήμα κάθε φορά! 🔺🟦⭕",
  flagsTitle: "🏳️ Σημαίες του κόσμου",
  flagsSubtitle: "Ζωγράφισε μια σημαία, ένα σχήμα κάθε φορά! 🖍️",
  paintingsTitle: "🖼️ Διάσημοι πίνακες",
  paintingsSubtitle: "Φτιάξε ένα αριστούργημα, ένα σκίτσο κάθε φορά! 🎨",

  feedbackAria: "Στείλε μας τα σχόλιά σου",
  feedbackTitle: "💌 Πες μας τη γνώμη σου",
  feedbackIntro:
    "Μεγάλοι: θα θέλαμε πολύ να μάθουμε πώς να κάνουμε το Learn 2 Draw ακόμα καλύτερο!",
  feedbackSendAnother: "Στείλε άλλο",
  feedbackRatingPick: "— διάλεξε ένα (προαιρετικό) —",
  feedbackRatings: [
    "⭐⭐⭐⭐⭐ Το λατρέψαμε",
    "⭐⭐⭐⭐ Πολύ καλό",
    "⭐⭐⭐ Εντάξει",
    "⭐⭐ Θέλει δουλειά",
    "⭐ Δεν μας ταιριάζει",
  ],
  feedbackErrMsg: "Ουπς — κάτι πήγε στραβά. Δοκίμασε ξανά σε λίγο. 🙏",
  feedbackThanks: "Ευχαριστούμε πολύ! Το μήνυμά σου είναι καθ' οδόν. 💛",
  feedbackHowFun: "Πόσο διασκεδαστικό ήταν;",
  feedbackMessage: "Το μήνυμά σου",
  feedbackMessagePlaceholder: "Τι σου άρεσε; Τι θα μπορούσε να γίνει καλύτερο;",
  feedbackName: "Το όνομά σου (προαιρετικό)",
  feedbackNamePlaceholder: "Γιάννης",
  feedbackEmail: "Email (προαιρετικό — για να απαντήσουμε)",
  feedbackSending: "Αποστολή… ✉️",
  feedbackSend: "Στείλε τα σχόλια 💌",
  feedbackError: "Κάτι πήγε στραβά. Δοκίμασε ξανά.",
  madeWithLove: "Φτιαγμένο με ❤️ για χαρούμενους μικρούς καλλιτέχνες 🖍️",

  trophyTitle: "🏆 Το ράφι με τα αυτοκόλλητά μου",
  trophyTally: (done, all) => `Κέρδισες ${done} από ${all} αυτοκόλλητα!`,
  trophyAllDone: "🎉 Τα ζωγράφισες όλα!",
  trophyKeepGoing: "Συνέχισε να ζωγραφίζεις για να τα μαζέψεις! ✨",
  trophyTapHint: "Πάτησε ένα ξεθωριασμένο (ή πες το όνομά του) για να το ζωγραφίσεις! ✏️",
  trophyResetConfirm: "Να σβήσουμε όλα τα αυτοκόλλητα και να ξεκινήσουμε ξανά;",
  trophyResetYes: "Ναι, μηδενισμός",
  trophyResetNo: "Κράτησέ τα",
  trophyResetBtn: "🔄 Ξεκίνα από την αρχή",
  trophyFriends: "🎁 Φίλοι ζώα",
  trophyFriendsNext: (n, article, name, emoji) =>
    `Ένας νέος φίλος ζώο κάθε 5 σχέδια — ${n} ακόμα για να ξεκλειδώσεις ${article}${name}! ${emoji}`,
  trophyFriendsAll: "Ουάου! Μάζεψες όλους τους φίλους ζώα! 🦕🎉",
  trophyTapFriend: "Πάτησε έναν φίλο για να τον ζωγραφίσεις! ✏️",
  trophyDrawingsToUnlock: (n) => `${n} σχέδια`,
  trophyDrawFriendAria: (article, name) => `Ζωγράφισε ${article}${name}`,
  trophyDrawFriendTitle: (article, name) => `Ζωγράφισε ${article}${name}! ✏️`,
  trophyFriendLocked: (name, need) => `${name} — ζωγράφισε ${need} για να ξεκλειδώσει`,
  trophyCups: "⚽ Κύπελλα ποδοσφαίρου",
  trophyCupsNext: (n, article, name, emoji) =>
    `Κέρδισε ένα κύπελλο ζωγραφίζοντας σημαίες του Μουντιάλ — ${n} ακόμα για ${article}${name}! ${emoji}`,
  trophyCupsAll: "Πρωταθλητής! Κέρδισες όλα τα κύπελλα! 🏆🎉",
  trophyCupLocked: (name, need) =>
    `${name} — ζωγράφισε ${need} σημαίες του Μουντιάλ για να το κερδίσεις`,
  trophyCupFlags: (need) => `${need} σημαίες`,
  trophyGroupEasy: "🌱 Εύκολο",
  trophyGroupMedium: "🌳 Μεσαίο",
  trophyGroupHarder: "⭐ Δύσκολο",
  trophyGroupPaintings: "🖼️ Πίνακες",
  trophyGroupFlags: "🏳️ Σημαίες",
  cardDrawAria: (_gender, name) => `Ζωγράφισε: ${name}`,
  cardDrawDoneAria: (_gender, name) => `Ζωγράφισε: ${name} (έτοιμο!)`,
  trophyStickerDone: (name) => `${name} — έτοιμο!`,
  trophyStickerLockedAria: (_gender, name) => `Ζωγράφισε: ${name} — δεν έγινε ακόμα`,

  footerPrivacy: "🔒 Ιδιωτικότητα",
  footerHello: "👋 Πες γεια",
  footerRules: "📜 Κανόνες",
  footerAbout: "ℹ️ Σχετικά",
  youtubeChannel: "📺 Κανάλι YouTube",
  coffeeBtn: "☕ Κέρασέ με έναν καφέ",
  coffeeNote:
    "Σου αρέσει ο ιστότοπος; Βοήθησέ τον να μείνει δωρεάν και να αποκτήσει κι άλλα σχέδια 💛",
  footerLove: " — φτιαγμένο με ❤️ για μικρούς καλλιτέχνες, και μαζί τους 🖍️",
  footerCopyright: (year) => `© ${year} Learn 2 Draw — με επιφύλαξη κάθε δικαιώματος.`,

  tlCodeTitle: "Κωδικός μεγάλων 🔒",
  tlCodeEnter: "Βάλε τον 4ψήφιο κωδικό σου για να ανοίξεις τις ρυθμίσεις",
  tlWrongCode: "Λάθος κωδικός",
  tlSetCodeTitle: "Όρισε έναν κωδικό μεγάλων 🔒",
  tlMakeCode: "Σκέψου έναν 4ψήφιο κωδικό",
  tlConfirmCode: "Βάλ' τον ξανά για επιβεβαίωση",
  tlCodesMismatch: "Οι κωδικοί δεν ταιριάζουν — ξεκίνα ξανά",
  tlLimitTitle: "⏱️ Ημερήσιο όριο χρόνου",
  tlOff: "Όχι",
  tlOn: "Ναι",
  tlLimitOff: "Κανένα",
  tlMin: (m) => `${m} λεπτά`,
  tlUsedToday: "Χρόνος σήμερα:",
  tlOfLimit: (m) => ` από ${m} λεπτά`,
  tlNoLimit: " (χωρίς όριο)",
  tlKeepFullscreen: "🖥️ Παραμονή σε πλήρη οθόνη",
  tlFsUnsupported: "Η πλήρης οθόνη δεν υποστηρίζεται σε αυτή τη συσκευή.",
  tlFewer: "🙈 Λιγότερες διασπάσεις προσοχής",
  tlFewerNote:
    "Κρύβει τους συνδέσμους στο κάτω μέρος και το «Εκτύπωση για χρωματισμό». Ενεργό αυτόματα όταν υπάρχει όριο χρόνου ή πλήρης οθόνη.",
  tlLockSettings: "🔒 Κλείδωσε τις ρυθμίσεις με κωδικό",
  tlResetTimer: "↺ Μηδένισε το σημερινό χρονόμετρο",
  tlNewCodeOnDone: "Νέος κωδικός στο «Τέλος»",
  tlChangeCode: "Άλλαξε κωδικό",
  tlDone: "Τέλος",
  tlClose: "Κλείσιμο",
  tlEscHint: "Πάτησε ξανά Esc για έξοδο ✏️",
  lockTimesUpAria: "Ο χρόνος τελείωσε",
  lockTimesUp: "Ο χρόνος τελείωσε!",
  lockTimesUpBody:
    "Ζωγράφισες πολύ ωραία σήμερα! Ρώτησε έναν μεγάλο αν θέλεις να συνεχίσεις.",
  lockEnterCode: "Βάλε τον 4ψήφιο κωδικό",
  lockWrongCode: "Λάθος κωδικός — δοκίμασε ξανά",
  fsAria: "Πλήρης οθόνη",
  fsTitle: "Συνέχισε να ζωγραφίζεις!",
  fsBody: "Πάτησε το κουμπί για να επιστρέψεις σε πλήρη οθόνη.",
  fsEnterCode: "Βάλε τον 4ψήφιο κωδικό για έξοδο",
  exitAria: "Έξοδος από πλήρη οθόνη",
  exitTitle: "Έξοδος από πλήρη οθόνη;",
  exitBody: "Βάλε τον κωδικό των μεγάλων για να βγεις.",
  pinDelete: "Διαγραφή",
  pinHint: (first, sum) =>
    `Βοήθεια: ξεκινά με ${first}, και τα τέσσερα ψηφία αθροίζουν ${sum}.`,
  lockKeepGoing: "🔓 Συνέχισε",
  lockGrownUpCode: "🔓 Κωδικός μεγάλων",
  fsBackBtn: "▶ Πίσω σε πλήρη οθόνη",
  fsGrownUpExit: "Μεγάλος: έξοδος",
  exitBtn: "🔓 Έξοδος από πλήρη οθόνη",
  exitKeepDrawing: "✏️ Συνέχισε να ζωγραφίζεις",

  commands: {
    up: ["πάνω", "πανω"],
    down: ["κάτω", "κατω"],
    shapes: ["σχήματα", "σχήμα", "σχηματα"],
    facts: ["στοιχεία", "ήξερες", "ξερες"],
    why: ["γιατί", "γιατι"],
    paintings: ["πίνακες", "πίνακας", "ζωγραφιά", "τέχνη"],
    flags: ["σημαίες", "σημαία", "σημαιες"],
    trophies: ["αυτοκόλλητα", "αυτοκόλλητο", "τρόπαια", "βραβεία"],
    print: ["εκτύπωση", "χρωματισμός"],
    privacy: ["ιδιωτικότητα"],
    contact: ["επικοινωνία", "γεια"],
    terms: ["κανόνες", "όροι"],
    dark: ["σκοτεινό", "νύχτα"],
    light: ["φωτεινό", "μέρα"],
    home: ["αρχική", "σπίτι"],
    back: ["πίσω"],
    next: ["επόμενο", "μπροστά"],
  },
};

const nl: Strings = {
  brandTagline:
    "Laten we stap voor stap leren tekenen — een gratis tekensite voor kinderen 💛",
  paperNoteStrong: "Pak een pen en papier!",
  paperNoteRest:
    " Het scherm is alleen de juf — kijk hoe het tekent, en teken het dan echt op papier. Daar gebeurt de magie. ✨",

  whyDraw: "Waarom tekenen?",
  whyDrawAria: "Waarom tekenen geweldig is",
  funFacts: "Leuke weetjes",
  funFactsAria: "Leuke weetjes",
  myStickers: "Mijn stickers",
  myStickersAria: "Mijn stickers",

  pickCta: "Kies iets om te tekenen 👇",
  chooseDifficulty: "Kies een niveau",
  levelEasy: "Makkelijk",
  levelMedium: "Gemiddeld",
  levelHarder: "Moeilijker",
  levelDrawingsAria: (label) => `Tekeningen niveau ${label.toLowerCase()}`,

  flags: "Vlaggen",
  flagsAria: "Vlaggen van de wereld",
  filterByCategory: "Filter makkelijke tekeningen op categorie",
  catAll: "Alles",
  catAllAria: "Alle makkelijke tekeningen",
  catShapes: "Vormen",
  catShapesAria: "Vormen",
  catPaintings: "Schilderij",
  catPaintingsAria: "Beroemde schilderijen",

  settings: "⚙️ Instellingen",
  forGrownUps: "Voor volwassenen",
  forGrownUpsAria: "Voor volwassenen — dagelijkse tijdslimiet en schermvergrendeling",
  printGroupAria: "Voor volwassenen — kleurplaten afdrukken",
  printMaking: "⏳ PDF maken…",
  printCta: "🖨️ Kleurplaten afdrukken",
  scrollUp: "Omhoog",
  scrollDown: "Omlaag",

  home: "🏠 Thuis",
  backToHome: "Terug naar thuis",
  homeShapes: "⭕ Vormen",
  homePaintings: "🖼️ Schilderijen",
  homeFlags: "🏳️ Vlaggen",
  homeStickers: "🏆 Stickers",
  backToShapes: "Terug naar vormen",
  backToPaintings: "Terug naar schilderijen",
  backToFlags: "Terug naar vlaggen",
  backToStickers: "Terug naar mijn stickers",
  by: "door",
  howToDraw: (subject) => `Zo teken je een ${subject}`,
  stepOf: (i, n) => `Stap ${i} / ${n}`,
  previousStep: "Vorige stap",
  nextStep: "Volgende stap",
  playDrawing: "Start de tekening",
  pauseDrawing: "Pauzeer de tekening",
  drawingSpeed: "Tekensnelheid",
  modeAnimate: "Animatie",
  modeStill: "Stilstaand beeld",
  switchToStill: "Toon een stilstaand beeld",
  switchToAnimation: "Bekijk hoe het zichzelf tekent",
  hintKeysBefore: "Druk op ",
  hintKeysMiddle: " voor volgende, ",
  hintKeysAfter: " om terug te gaan",
  watchStart: "Auto Afspelen",
  watchStop: "Stop",
  watchStartAria: "Start handsfree automatisch afspelen",
  watchStopAria: "Stop handsfree automatisch afspelen",
  yourSketch: "Jouw schets ✏️",
  realPainting: "Het echte schilderij 🖼️",

  allDoneWatchAgain: "🎉 Klaar! Laten we het nog een keer bekijken…",
  allDoneSurprise: "Jee, helemaal klaar! ⭐ Druk op ▶ voor een verrassing! 🎉",
  allDoneExport: "Jee, helemaal klaar! 🎉",
  nextColour: "Nu de volgende kleur! 🖍️",

  youDidIt: "Het is gelukt!",
  youDrew: () => "Je tekende… ",
  youDrewSuffix: "! 🥳",
  levelUpTitle: (level) => `${level}-modus voltooid! 🏆`,
  levelUpAllDone: (level) => ` — en alle ${level.toLowerCase()}-tekeningen afgerond! 🥳`,
  levelUpReady: (level) => `Klaar voor ${level.toLowerCase()}-modus? Daar gaan we! 🚀`,
  newFriend: "Nieuwe vriend vrijgespeeld! 🎉",
  youEarned: (article) => `Je hebt ${article}`,
  youEarnedSuffix: " gewonnen!",
  drawYourReward: (_article, name) => `Teken ${_article}${name}! ✏️`,
  pickAnother: "🎨 Kies een andere",
  watchAgain: "↺ Nog eens kijken",
  nextLabel: "Volgende:",
  startingIn: (secs) => `start over ${secs} sec.`,
  goBackToDrawing: "Terug naar de tekening",

  voiceOn: "Ik luister…",
  voiceOff: "Praat tegen me",
  voiceOnAria: "Zet spraakbesturing uit",
  voiceOffAria: "Zet spraakbesturing aan",
  voiceUnsupported: "🎤 Spraak wordt niet ondersteund in deze browser",
  voiceLegendSayName: "Zeg een naam",
  soundsOn: "Geluiden aan",
  soundsOff: "Geluiden uit",
  soundsOnAria: "Zet geluiden uit",
  soundsOffAria: "Zet geluiden aan",
  readAloudOn: "Voorlezen aan",
  readAloudOff: "Voorlezen uit",
  readAloudOnAria: "Zet voorlezen uit",
  readAloudOffAria: "Zet voorlezen aan",
  autoPlayOn: "Auto Afspelen aan",
  autoPlayOff: "Auto Afspelen uit",
  autoPlayOnAria: "Zet auto afspelen uit",
  autoPlayOffAria: "Zet auto afspelen aan",
  readAloudConfirm: "Voorlezen staat aan!",
  themeLight: "☀️ Licht",
  themeDark: "🌙 Donker",
  themeLightAria: "Schakel naar lichte modus",
  themeDarkAria: "Schakel naar donkere modus",

  switchTo: (name) => `Schakel naar ${name}`,
  languageName: { en: "Engels", fr: "Frans", el: "Grieks", nl: "Nederlands" },

  forGrownUpsLead: "Voor volwassenen:",
  factsTitle: "💡 Leuke tekenweetjes",
  whyTitle: "🎨 Waarom tekenen geweldig is",
  whySubtitle: "Kleine krabbels, grote voordelen! 🌱",
  whyIntro:
    "Tekenen is een van de rijkste activiteiten die een jong kind kan doen, en het bouwt stilletjes vaardigheden op die veel verder reiken dan kunst.",
  whyOutro:
    "Het mooiste: er is geen verkeerde manier. Prijs de inzet en het proberen, geef de kleurpotloden, en laat ze gaan. 💛",
  privacyTitle: "🔒 Jouw privacy",
  privacySubtitle: "Veilig, eenvoudig, en gewoon voor de lol! 🌈",
  privacyGrownUps:
    "Learn 2 Draw is een statische website zonder backend, zonder cookies, en zonder reclame of cross-site trackers. Voortgang en de ouderlijk toezicht (pincode, dagelijkse tijdslimiet, volledig scherm) worden alleen opgeslagen in de lokale opslag van deze browser en worden nooit verzonden. Wij gebruiken Cloudflare Web Analytics — een privacyvriendelijke, cookieloze dienst die paginaweergaven in totaal telt en geen persoonlijke gegevens verzamelt of bezoekersprofielen opbouwt. Als u het feedbackformulier gebruikt, worden de naam, het e-mailadres en het bericht dat u invoert alleen per e-mail naar ons verzonden via Web3Forms (een formulierbezorgdienst) wanneer u het verstuurt. De optionele spraakbesturing gebruikt de ingebouwde Web Speech API van de browser, die audio naar de browsermaker kan sturen voor herkenning zolang deze aan staat. De site wordt gehost op Firebase Hosting, dat IP-adressen van bezoekers verwerkt in standaard serverlogs als host.",
  termsTitle: "📜 Vriendelijke regels",
  termsSubtitle: "Een paar fijne huisregels 🌟",
  termsGrownUps:
    "Learn 2 Draw wordt gratis aangeboden, 'as is', voor persoonlijk, niet-commercieel gebruik, zonder enige garantie. Alle broncode en originele artwork zijn eigendom van de auteur en mogen niet worden gereproduceerd, herverdeeld of commercieel gebruikt zonder schriftelijke toestemming. De meegeleverde schilderijafbeeldingen zijn reproducties uit het publieke domein. Door de site te gebruiken accepteert u dat het een speels educatief hulpmiddel is en dat de auteur niet aansprakelijk is voor enig verlies dat voortvloeit uit het gebruik ervan.",
  contactTitle: "👋 Zeg hallo!",
  contactSubtitle: "Een klein berichtje van ons voor jou 💛",
  shapesTitle: "⭕ Leer je vormen",
  shapesSubtitle:
    "Grote, simpele vormen voor kleine kunstenaars — teken één reusachtige vorm tegelijk! 🔺🟦⭕",
  flagsTitle: "🏳️ Vlaggen van de wereld",
  flagsSubtitle: "Teken een vlag, één vorm tegelijk! 🖍️",
  paintingsTitle: "🖼️ Beroemde schilderijen",
  paintingsSubtitle: "Maak een meesterwerk, één schets tegelijk! 🎨",

  feedbackAria: "Stuur ons feedback",
  feedbackTitle: "💌 Laat ons weten wat je denkt",
  feedbackIntro:
    "Volwassenen: we horen graag hoe we Learn 2 Draw nog beter kunnen maken!",
  feedbackSendAnother: "Nog een bericht sturen",
  feedbackRatingPick: "— kies een (optioneel) —",
  feedbackRatings: [
    "⭐⭐⭐⭐⭐ Geweldig",
    "⭐⭐⭐⭐ Heel goed",
    "⭐⭐⭐ Best oké",
    "⭐⭐ Kan beter",
    "⭐ Niet voor ons",
  ],
  feedbackErrMsg: "Oeps — er is iets misgegaan. Probeer het straks opnieuw. 🙏",
  feedbackThanks: "Heel erg bedankt! Je bericht is onderweg. 💛",
  feedbackHowFun: "Hoe leuk was het?",
  feedbackMessage: "Je bericht",
  feedbackMessagePlaceholder: "Wat vond je leuk? Wat kan er beter?",
  feedbackName: "Je naam (optioneel)",
  feedbackNamePlaceholder: "Sam",
  feedbackEmail: "E-mail (optioneel — zodat we kunnen antwoorden)",
  feedbackSending: "Verzenden… ✉️",
  feedbackSend: "Verstuur feedback 💌",
  feedbackError: "Er is iets misgegaan. Probeer het opnieuw.",
  madeWithLove: "Gemaakt met ❤️ voor blije kleine kunstenaars 🖍️",

  trophyTitle: "🏆 Mijn stickerplank",
  trophyTally: (done, all) => `Je hebt ${done} van de ${all} stickers verdiend!`,
  trophyAllDone: "🎉 Je hebt ze allemaal getekend!",
  trophyKeepGoing: "Blijf tekenen om ze te verzamelen! ✨",
  trophyTapHint: "Tik op een vervaagde (of zeg de naam) om 'm te tekenen! ✏️",
  trophyResetConfirm: "Al je stickers wissen en opnieuw beginnen?",
  trophyResetYes: "Ja, wissen",
  trophyResetNo: "Houden",
  trophyResetBtn: "🔄 Opnieuw beginnen",
  trophyFriends: "🎁 Dierenvrienden",
  trophyFriendsNext: (n, _article, name, emoji) =>
    `Elke 5 tekeningen een nieuwe dierenvriend — nog ${n} om ${_article}${name} vrij te spelen! ${emoji}`,
  trophyFriendsAll: "Wauw! Je hebt alle dierenvrienden verzameld! 🦕🎉",
  trophyTapFriend: "Tik op een vriend om 'm te tekenen! ✏️",
  trophyDrawingsToUnlock: (n) => `${n} tekeningen`,
  trophyDrawFriendAria: (_article, name) => `Teken de ${name}`,
  trophyDrawFriendTitle: (_article, name) => `Teken de ${name}! ✏️`,
  trophyFriendLocked: (name, need) => `${name} — teken ${need} om vrij te spelen`,
  trophyCups: "⚽ Voetbalbekers",
  trophyCupsNext: (n, _article, name, emoji) =>
    `Win een beker door WK-vlaggen te tekenen — nog ${n} voor ${_article}${name}! ${emoji}`,
  trophyCupsAll: "Kampioen! Je hebt alle bekers gewonnen! 🏆🎉",
  trophyCupLocked: (name, need) => `${name} — teken ${need} WK-vlaggen om te winnen`,
  trophyCupFlags: (need) => `${need} vlaggen`,
  trophyGroupEasy: "🌱 Makkelijk",
  trophyGroupMedium: "🌳 Gemiddeld",
  trophyGroupHarder: "⭐ Moeilijker",
  trophyGroupPaintings: "🖼️ Schilderijen",
  trophyGroupFlags: "🏳️ Vlaggen",
  cardDrawAria: (_gender, name) => `Teken een ${name}`,
  cardDrawDoneAria: (_gender, name) => `Teken een ${name} (klaar!)`,
  trophyStickerDone: (name) => `${name} — klaar!`,
  trophyStickerLockedAria: (_gender, name) => `Teken een ${name} — nog niet klaar`,

  footerPrivacy: "🔒 Privacy",
  footerHello: "👋 Zeg hallo",
  footerRules: "📜 Regels",
  footerAbout: "ℹ️ Over ons",
  youtubeChannel: "📺 YouTube-kanaal",
  coffeeBtn: "☕ Koop me een koffie",
  coffeeNote: "Vind je de site leuk? Help hem gratis te blijven en meer tekeningen te krijgen 💛",
  footerLove: " — gemaakt met ❤️ voor kleine kunstenaars, en samen met hen 🖍️",
  footerCopyright: (year) => `© ${year} Learn 2 Draw — alle rechten voorbehouden.`,

  tlCodeTitle: "Volwassenencode 🔒",
  tlCodeEnter: "Voer je 4-cijferige code in om de instellingen te openen",
  tlWrongCode: "Verkeerde code",
  tlSetCodeTitle: "Stel een volwassenencode in 🔒",
  tlMakeCode: "Verzin een 4-cijferige code",
  tlConfirmCode: "Voer 'm nog een keer in om te bevestigen",
  tlCodesMismatch: "Codes kwamen niet overeen — begin opnieuw",
  tlLimitTitle: "⏱️ Dagelijkse tijdslimiet",
  tlOff: "Uit",
  tlOn: "Aan",
  tlLimitOff: "Uit",
  tlMin: (m) => `${m} min`,
  tlUsedToday: "Vandaag gebruikt:",
  tlOfLimit: (m) => ` van ${m} min`,
  tlNoLimit: " (geen limiet ingesteld)",
  tlKeepFullscreen: "🖥️ Blijf op volledig scherm",
  tlFsUnsupported: "Volledig scherm wordt niet ondersteund op dit apparaat.",
  tlFewer: "🙈 Minder afleiding",
  tlFewerNote:
    "Verbergt de footerlinks en 'Afdrukken om in te kleuren'. Staat automatisch aan als een tijdslimiet of volledig scherm is ingesteld.",
  tlLockSettings: "🔒 Vergrendel instellingen met een code",
  tlResetTimer: "↺ Reset de timer van vandaag",
  tlNewCodeOnDone: "Nieuwe code bij Gereed",
  tlChangeCode: "Code wijzigen",
  tlDone: "Gereed",
  tlClose: "Sluiten",
  tlEscHint: "Druk nogmaals op Esc om te verlaten ✏️",
  lockTimesUpAria: "Tijd voorbij",
  lockTimesUp: "Tijd voorbij!",
  lockTimesUpBody: "Goed getekend vandaag! Vraag een volwassene als je door wilt gaan.",
  lockEnterCode: "Voer de 4-cijferige code in",
  lockWrongCode: "Verkeerde code — probeer opnieuw",
  fsAria: "Volledig scherm",
  fsTitle: "Blijf tekenen!",
  fsBody: "Tik op de knop om terug te gaan naar volledig scherm.",
  fsEnterCode: "Voer de 4-cijferige code in om te verlaten",
  exitAria: "Volledig scherm verlaten",
  exitTitle: "Volledig scherm verlaten?",
  exitBody: "Voer de volwassenencode in om te vertrekken.",
  pinDelete: "Verwijderen",
  pinHint: (first, sum) =>
    `Hint: begint met ${first}, en de vier cijfers tellen op tot ${sum}.`,
  lockKeepGoing: "🔓 Doorgaan",
  lockGrownUpCode: "🔓 Volwassenencode",
  fsBackBtn: "▶ Terug naar volledig scherm",
  fsGrownUpExit: "Volwassene: verlaat",
  exitBtn: "🔓 Volledig scherm verlaten",
  exitKeepDrawing: "✏️ Blijf tekenen",

  commands: {
    up: ["omhoog", "boven"],
    down: ["omlaag", "beneden"],
    shapes: ["vormen", "vorm"],
    facts: ["weetjes", "feiten"],
    why: ["waarom"],
    paintings: ["schilderijen", "schilderij", "kunst", "beroemd"],
    flags: ["vlaggen", "vlag"],
    trophies: ["stickers", "sticker", "trofeen", "trofeeën"],
    print: ["printen", "kleuren", "kleurplaat"],
    privacy: ["privacy"],
    contact: ["contact", "hallo", "zeg hallo"],
    terms: ["regels", "voorwaarden"],
    dark: ["donker", "nacht"],
    light: ["licht", "dag"],
    home: ["home", "thuis"],
    back: ["terug"],
    next: ["volgende"],
  },
};

export const STRINGS: Record<Locale, Strings> = { en, fr, el, nl };
